import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { X, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

const DISMISSED_KEY = "exit-intent-dismissed";
const COOLDOWN_DAYS = 3;

export const ExitIntentPopup = () => {
  const [open, setOpen] = useState(false);

  const dismiss = useCallback(() => {
    setOpen(false);
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  }, []);

  useEffect(() => {
    // Skip if recently dismissed
    const ts = localStorage.getItem(DISMISSED_KEY);
    if (ts && Date.now() - Number(ts) < COOLDOWN_DAYS * 24 * 60 * 60 * 1000) return;

    let fired = false;

    const handleMouseLeave = (e: MouseEvent) => {
      if (fired) return;
      // Trigger when cursor moves above the viewport (toward browser chrome / close button)
      if (e.clientY <= 0) {
        fired = true;
        setOpen(true);
      }
    };

    // Only attach on desktop — exit-intent doesn't apply to touch devices
    if (window.matchMedia("(pointer: fine)").matches) {
      document.addEventListener("mouseout", handleMouseLeave);
    }

    return () => document.removeEventListener("mouseout", handleMouseLeave);
  }, []);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="sm:max-w-md gap-6 p-6 md:p-8">
        <DialogTitle className="sr-only">Before you go</DialogTitle>

        <div className="text-center space-y-3">
          <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-primary/10">
            <Clock className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-xl font-bold text-foreground">
            Don't get stuck in a long TSA line!
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            Create a free account to save your airports, get real-time wait alerts, and know exactly when to leave for your flight.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <Button asChild size="lg" className="w-full min-h-[48px] gap-2">
            <Link to="/auth" onClick={dismiss}>
              Create Free Account <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={dismiss}
            className="text-muted-foreground hover:text-foreground"
          >
            No thanks, I'll risk it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
